import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

export const Solution = () => {
  const benefits = [
    'Schedule meetings with a message',
    'Set reminders in seconds',
    'Summarise documents instantly',
    'Upload tickets & PDFs — retrieve PNRs, dates, or any detail in plain language',
    'Manage your entire calendar',
    'Works in English, Hindi & Hinglish',
    'Available 24/7, never sleeps',
  ];

  return (
    <section className="py-24 lg:py-32" data-testid="solution-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#25D366] font-semibold text-sm uppercase tracking-wider">The Solution</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              What if one WhatsApp message
              <br />
              <span className="gradient-text">could replace all of them?</span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              AssistlyAI brings your entire workday into the one app you already use every single day —
              meetings, reminders, notes, and saved documents you can query anytime. No new apps. No learning curve.
              Just send a message.
            </p>

            {/* Benefits List */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-[#25D366]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#25D366]" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10"
            >
              <a
                href="https://wa.me/919981361957?text=Hi%20AssistlyAI!"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-green-500/30"
                data-testid="solution-cta"
              >
                Try AssistlyAI Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative max-w-md mx-auto">
              {/* WhatsApp UI */}
              <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700">
                {/* Header */}
                <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/assistlylogo.png`}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover bg-white shrink-0"
                  />
                  <div>
                    <p className="text-white font-semibold">AssistlyAI</p>
                    <p className="text-emerald-200 text-sm">Your AI Assistant</p>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="bg-[#ECE5DD] dark:bg-slate-900 p-4 space-y-3">
                  <div className="flex justify-end">
                    <div className="bg-[#DCF8C6] dark:bg-emerald-900 rounded-lg rounded-tr-none p-3 max-w-[75%]">
                      <p className="text-slate-800 dark:text-slate-100 text-sm">Schedule a meeting with Rahul for tomorrow at 11am</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-slate-800 rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                      <p className="text-slate-800 dark:text-slate-100 text-sm">
                        Done! Meeting with Rahul scheduled for tomorrow at 11:00 AM. 
                        <span className="block mt-1">📅 I've sent a calendar invite to both of you.</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <div className="bg-[#DCF8C6] dark:bg-emerald-900 rounded-lg rounded-tr-none p-3 max-w-[75%]">
                      <p className="text-slate-800 dark:text-slate-100 text-sm">Perfect, thanks!</p>
                    </div>
                  </div>

                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-slate-800 rounded-lg rounded-tl-none p-3 max-w-[80%] shadow-sm">
                      <p className="text-slate-800 dark:text-slate-100 text-sm">
                        You're welcome! Anything else I can help you with? 😊
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-6 -right-6 bg-[#25D366] text-white rounded-2xl px-4 py-2 shadow-lg"
              >
                <span className="font-semibold">One app. Everything done.</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
